import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '../lib/cx';
import { Button } from './Button';
import styles from './Card.module.css';

interface CardProps {
  readonly title: string;
  readonly hint?: string;
  /** Right side of the header (e.g. a toggle). */
  readonly extra?: ReactNode;
  readonly loading?: boolean;
  /** Set to show the error state instead of the content. */
  readonly error?: boolean;
  readonly errorText?: string;
  readonly onRetry?: () => void;
  readonly className?: string;
  readonly testId?: string;
  readonly children: ReactNode;
}

/** Card frame with the three states of ui-spec §3: content (default or empty), loading, error. */
export function Card({
  title,
  hint,
  extra,
  loading = false,
  error = false,
  errorText,
  onRetry,
  className,
  testId,
  children,
}: CardProps) {
  const { t } = useTranslation();
  return (
    <section
      className={cx(styles.card, className)}
      aria-label={title}
      aria-busy={loading}
      data-testid={testId}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        {extra}
      </header>
      {hint !== undefined && !loading && !error ? <p className={styles.hint}>{hint}</p> : null}
      {error ? (
        <div className={styles.error} role="alert">
          <p>{errorText}</p>
          {onRetry !== undefined ? <Button onClick={onRetry}>{t('common.retry')}</Button> : null}
        </div>
      ) : loading ? (
        <div className={styles.skeleton} aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      ) : (
        children
      )}
    </section>
  );
}
